import { memo, type FC } from 'react';
import { Spin } from 'antd';
import { useCameraById } from '../../hooks/use-camera-data';
import { CameraCard } from './CameraCard';

export interface CameraCardContainerProps {
  cameraId: string;
  onSettingsClick: () => void;
  onStartStop: () => void;
  onCardClick?: () => void;
  isMobile?: boolean;
}

/**
 * CameraCardContainer - Data fetching wrapper
 * Fetches individual camera data and passes to CameraCard
 * Memoized to prevent unnecessary re-renders when handler functions are stable
 */
export const CameraCardContainer: FC<CameraCardContainerProps> = memo(
  ({ cameraId, onSettingsClick, onStartStop, onCardClick, isMobile = false }) => {
    const { data: camera, isLoading } = useCameraById(cameraId);

    if (isLoading || !camera) {
      return (
        <div
          style={{
            width: isMobile ? 'auto' : '100%',
            height: isMobile ? 'calc(100% - 8px)' : '100%',
            maxHeight: isMobile ? '100%' : 'none',
            aspectRatio: '176 / 336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: isMobile ? '16px' : '12px',
          }}
        >
          <Spin />
        </div>
      );
    }

    return (
      <CameraCard
        camera={camera}
        onSettingsClick={onSettingsClick}
        onStartStop={onStartStop}
        onCardClick={onCardClick}
        isMobile={isMobile}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if props actually changed
    return (
      prevProps.cameraId === nextProps.cameraId &&
      prevProps.onSettingsClick === nextProps.onSettingsClick &&
      prevProps.onStartStop === nextProps.onStartStop &&
      prevProps.onCardClick === nextProps.onCardClick &&
      prevProps.isMobile === nextProps.isMobile
    );
  }
);

CameraCardContainer.displayName = 'CameraCardContainer';
